import { omit } from 'es-toolkit';
import { mutationOptions } from '../../common/utils';
import { CART_QUERY_KEYS } from './keys';
import type {
  GetCartRequest,
  AddCartItemsRequest,
  AddToCartRequest,
  UpdateCartItemRequest,
  DeleteCartRequest,
  ApplyCouponRequest,
  RemoveCouponRequest,
} from './types';
import { cartAPIClient } from '../cartAPIClient';

import { cartTokenQueue } from './cartTokenQueue';
import type { QueryClient } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';
import type { CartResponseResponse } from '../../../model/cart/cart';
import { persistCartSessionKey } from '../../../utils/sessionKeyPersistence';

export type CartServiceMutationProps = GetCartRequest & {
  queryClient: QueryClient;
};

const setGeneratedSessionKey = (data: CartResponseResponse['generatedSessionKey']) => {
  if (data?.key && data?.value) {
    // Persist via registered persistence (web: cookie, RN: AsyncStorage, etc.)
    void persistCartSessionKey({
      name: data.key,
      value: data.value,
    });
  }
};

const applyCartMutationResponse = (
  queryClient: QueryClient,
  { currency, sessionKey }: Pick<CartServiceMutationProps, 'currency' | 'sessionKey'>,
  data: CartResponseResponse,
) => {
  setGeneratedSessionKey(data.generatedSessionKey);
  cartTokenQueue.enqueue(data.cartInfo.cartToken);

  // A mutation fired before a country change can resolve after it. Writing that
  // stale-currency cart under the new currency's key poisons the cache, and since
  // setQueryData resets freshness it also suppresses the corrective refetch.
  const responseMatchesRequestedCurrency = data.cart.currency.currency === currency;
  if (responseMatchesRequestedCurrency) {
    queryClient.setQueryData(CART_QUERY_KEYS.getCart({ currency, sessionKey }), data);
  } else {
    void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEYS.default() });
  }
};

export const TEMP_ITEM_KEY = 'TEMP_PRODUCT';

export const CART_QUERIES = {
  getCart: ({ currency, cartToken, sessionKey }: GetCartRequest) => {
    return queryOptions({
      queryKey: CART_QUERY_KEYS.getCart({ currency, sessionKey }),
      // Adopting the response's identity belongs to the request, not to reading the cache:
      // `select` re-runs on every cached read, so a logout that clears the queue could see it
      // refilled with the signed-out user's token, and a cancelled refetch still applied its
      // response. Passing the signal lets a cancelled request be dropped before it answers.
      queryFn: async ({ signal }) => {
        const cart = await cartAPIClient.getCart({ sessionKey, currency, cartToken }, { signal });
        cartTokenQueue.enqueue(cart.cartInfo.cartToken);
        setGeneratedSessionKey(cart.generatedSessionKey);
        return cart;
      },
    });
  },

  addToCart: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (payload: AddToCartRequest) => {
        return cartAPIClient.addToCart({
          ...payload,
          currency,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
        });
      },
      mutationKey: CART_QUERY_KEYS.addToCart(),
      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },

  /**
   * Bulk add. Unlike `addToCart` this sends no `sessionKey` — `/cart/items` rejects unknown
   * properties and reads the cart from `cartToken` alone, the same token every other cart call
   * already depends on.
   */
  addItemsToCart: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (payload: AddCartItemsRequest) => {
        return cartAPIClient.addItemsToCart({
          ...payload,
          currency,
          cartToken: cartTokenQueue.peek(),
        });
      },
      mutationKey: CART_QUERY_KEYS.addItemsToCart(),
      onSuccess: (data) => {
        if (data.generatedSessionKey) {
          setGeneratedSessionKey(data.generatedSessionKey);
        }
        cartTokenQueue.enqueue(data.cartInfo.cartToken);
        // `results` reports on this request, not the cart — it must not leak into the cart cache.
        queryClient.setQueryData(CART_QUERY_KEYS.getCart({ currency, sessionKey }), omit(data, ['results']));
      },
    });
  },

  updateCartItem: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: UpdateCartItemRequest) => {
        return cartAPIClient.updateCartItem({
          ...request,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.updateCartItem(),

      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },

  deleteCartItem: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: DeleteCartRequest) => {
        return cartAPIClient.deleteCartItem({
          ...request,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.removeCartItem(),
      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },

  applyCoupon: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: ApplyCouponRequest) => {
        const cartToken = request.cartToken || cartTokenQueue.peek();
        
        return cartAPIClient.applyCoupon({
          ...request,
          sessionKey,
          cartToken,
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.applyCoupon(),
      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },

  removeCoupon: ({ queryClient, currency, sessionKey }: CartServiceMutationProps) => {
    return mutationOptions({
      mutationFn: (request: RemoveCouponRequest) => {
        return cartAPIClient.removeCoupon({
          ...request,
          sessionKey,
          cartToken: cartTokenQueue.peek(),
          currency,
        });
      },
      mutationKey: CART_QUERY_KEYS.removeCoupon(),
      onSuccess: (data) => {
        applyCartMutationResponse(queryClient, { currency, sessionKey }, data);
      },
    });
  },
};
